import { useEffect } from "react";
import type { SlideEntry } from "./types";

interface Props {
  slides: SlideEntry[];
  currentIndex: number;
  open: boolean;
  onToggle: () => void;
  onSelect: (index: number) => void;
}

export const TOP_BAR_HEIGHT_PX = 56;

export function MobileNav({
  slides,
  currentIndex,
  open,
  onToggle,
  onSelect,
}: Props) {
  const appendixStartIndex = slides.findIndex(
    (s) => s.sectionBefore === "Appendices",
  );

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onToggle();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onToggle]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  return (
    <>
      <header
        className="fixed left-0 right-0 top-0 z-50 flex items-center gap-3 bg-blue-950 px-3 shadow-md ring-1 ring-blue-900"
        style={{ height: TOP_BAR_HEIGHT_PX }}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-label={open ? "Close slide index" : "Open slide index"}
          aria-expanded={open}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-white transition active:scale-95 active:bg-white/10"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
            aria-hidden="true"
          >
            {open ? (
              <>
                <line
                  x1="5"
                  y1="5"
                  x2="17"
                  y2="17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="17"
                  y1="5"
                  x2="5"
                  y2="17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </>
            ) : (
              <>
                <line
                  x1="4"
                  y1="7"
                  x2="18"
                  y2="7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="4"
                  y1="11"
                  x2="18"
                  y2="11"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="4"
                  y1="15"
                  x2="18"
                  y2="15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </>
            )}
          </svg>
        </button>

        <img
          src="https://public.apptonomy.ai/a7y-wordmark-ondark.png"
          srcSet="https://public.apptonomy.ai/a7y-wordmark-ondark.png 1x, https://public.apptonomy.ai/a7y-wordmark-ondark_2x.png 2x, https://public.apptonomy.ai/a7y-wordmark-ondark_3x.png 3x"
          alt="Apptonomy"
          className="h-5 w-auto"
        />
      </header>

      <div
        className={`fixed inset-0 z-40 bg-black/60 transition-opacity ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onToggle}
        aria-hidden="true"
      />

      <aside
        className={`fixed bottom-0 left-0 z-40 w-72 transform overflow-y-auto bg-blue-950 transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ top: TOP_BAR_HEIGHT_PX }}
        aria-label="Slide index"
        aria-hidden={!open}
      >
        <div className="flex min-h-full flex-col p-3">
          <ol className="flex flex-col gap-1.5">
            {slides.map((slide, i) => {
              const isActive = i === currentIndex;
              const isAppendix =
                appendixStartIndex !== -1 && i >= appendixStartIndex;
              const numberLabel = isAppendix
                ? `A-${String(i - appendixStartIndex + 1).padStart(2, "0")}`
                : String(i + 1).padStart(2, "0");
              return (
                <li key={slide.id}>
                  {slide.sectionBefore ? (
                    <div
                      className={`px-2 pb-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 ${
                        i === 0 ? "" : "pt-3"
                      }`}
                    >
                      {slide.sectionBefore}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onSelect(i)}
                    aria-current={isActive ? "true" : undefined}
                    className={`block w-full rounded-md text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${
                      isActive
                        ? "bg-white shadow-lg ring-2 ring-blue-500"
                        : "bg-white/85 shadow-sm ring-1 ring-transparent active:bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-2 px-3 py-3">
                      <span
                        className={`w-9 shrink-0 text-right text-xs tabular-nums ${
                          isActive
                            ? "font-bold text-blue-700"
                            : "font-semibold text-slate-400"
                        }`}
                      >
                        {numberLabel}
                      </span>
                      <span
                        className={`text-sm leading-tight ${
                          isActive
                            ? "font-bold text-slate-900"
                            : "font-semibold text-slate-700"
                        }`}
                      >
                        {slide.title}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </aside>
    </>
  );
}
